import React, { useState } from 'react';
import { Button } from './ui/button';
import { Check, Star, Sparkles, Clock, Shield, Headphones } from 'lucide-react';

const Pricing = () => {
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubscribe = async () => {
    setIsProcessing(true);
    // Mock payment processing
    setTimeout(() => {
      alert('Payment processing would happen here! This is a demo.');
      setIsProcessing(false);
    }, 2000);
  };

  const features = [
    "Unlimited pressure vessel drawings",
    "Cloud-based access from anywhere", 
    ".dwg file export",
    "Industry standard compliance",
    "Technical support",
    "Regular software updates",
    "Multi-user team access",
    "Priority customer support"
  ];

  return (
    <section id="pricing" className="py-20 bg-slate-50">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Pricing
            </h2>
            <p className="text-xl text-slate-600">
              Affordable Automation. Unbeatable Value.
            </p>
            <p className="text-lg text-slate-600 mt-4 max-w-3xl mx-auto">
              Invest in efficiency and transform your design process with GenieFab. 
              We believe in providing accessible solutions that deliver immediate ROI.
            </p>
          </div>

          {/* Pricing Card */}
          <div className="relative">
            {/* Launch Offer Banner */}
            <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 z-10">
              <div className="bg-gradient-to-r from-amber-400 to-orange-500 text-white px-6 py-2 rounded-full font-semibold shadow-lg flex items-center space-x-2">
                <Sparkles className="w-4 h-4" />
                <span>Limited Launch Offer!</span>
                <Sparkles className="w-4 h-4" />
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
              {/* Card Header */}
              <div className="bg-gradient-to-r from-slate-800 to-slate-700 text-white p-8 text-center">
                <h3 className="text-2xl font-bold mb-2">GenieFab Yearly Subscription</h3>
                <p className="text-slate-200">Pressure Vessel Automation Tool</p>
              </div>

              {/* Pricing Content */}
              <div className="p-8">
                {/* Price Display */}
                <div className="text-center mb-8">
                  <div className="flex items-center justify-center space-x-4 mb-4">
                    <div className="text-right">
                      <div className="text-lg text-slate-500 line-through">₹ XX,XXX</div>
                      <div className="text-sm text-slate-400">Regular Price</div>
                    </div>
                    <div className="text-center">
                      <div className="text-4xl md:text-5xl font-bold text-slate-900">₹ XX,XXX</div>
                      <div className="text-slate-600">per year</div>
                      <div className="inline-flex items-center space-x-1 mt-2 bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                        <Star className="w-3 h-3" />
                        <span>50% OFF</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Features List */}
                <div className="mb-8">
                  <h4 className="text-lg font-semibold text-slate-900 mb-4 text-center">What's Included:</h4>
                  <div className="grid md:grid-cols-2 gap-3">
                    {features.map((feature, index) => (
                      <div key={index} className="flex items-center space-x-3">
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                        <span className="text-slate-600">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Additional Benefits */}
                <div className="grid md:grid-cols-3 gap-6 mb-8 p-6 bg-slate-50 rounded-xl">
                  <div className="text-center">
                    <Clock className="w-8 h-8 text-slate-700 mx-auto mb-2" />
                    <div className="font-semibold text-slate-900">Save 90% Time</div>
                    <div className="text-sm text-slate-600">Hours vs Days</div>
                  </div>
                  <div className="text-center">
                    <Shield className="w-8 h-8 text-slate-700 mx-auto mb-2" />
                    <div className="font-semibold text-slate-900">99%+ Accuracy</div>
                    <div className="text-sm text-slate-600">Precision Guaranteed</div>
                  </div>
                  <div className="text-center">
                    <Headphones className="w-8 h-8 text-slate-700 mx-auto mb-2" />
                    <div className="font-semibold text-slate-900">Timely Support</div>
                    <div className="text-sm text-slate-600">Available</div>
                  </div>
                </div>

                {/* CTA Button */}
                <div className="text-center">
                  <Button 
                    size="lg"
                    className="w-full bg-slate-800 hover:bg-slate-700 text-white py-4 text-lg font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
                    
                  >
                    {isProcessing ? (
                      <span className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Processing...
                      </span>
                    ) : (
                      "Subscribe Now - Get 50% OFF"
                    )}
                  </Button>
                  <p className="text-sm text-slate-500 mt-3">
                    ⚡ Limited time offer • Secure payment
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Trust Indicators */}
          <div className="text-center mt-12">
            <p className="text-slate-600 mb-4">Trusted by engineering professionals worldwide</p>
            <div className="flex items-center justify-center space-x-8 text-slate-400">
              <div className="flex items-center space-x-2">
                <Shield className="w-4 h-4" />
                <span className="text-sm">Secure Payment</span>
              </div>
              <div className="flex items-center space-x-2">
                <Clock className="w-4 h-4" />
                <span className="text-sm">Instant Access</span>
              </div>
              <div className="flex items-center space-x-2">
                <Headphones className="w-4 h-4" />
                <span className="text-sm">Timely Support</span>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="mt-16 bg-white rounded-xl p-8 shadow-sm">
            <h3 className="text-xl font-bold text-slate-900 mb-6 text-center">Frequently Asked Questions</h3>
            <div className="space-y-4">
              <div>
                <div className="font-semibold text-slate-900 mb-2">How long is this special offer valid?</div>
                <div className="text-slate-600">This is a limited-time launch offer for early adopters. This offer is valid for first 50 users only.</div>
              </div>
              <div>
                <div className="font-semibold text-slate-900 mb-2">What file formats do you support?</div>
                <div className="text-slate-600">GenieFab generates industry-standard drawings in .dwg file formats that are compatible with all major CAD software platforms.</div>
              </div>
              <div>
                <div className="font-semibold text-slate-900 mb-2">Is there a free trial available?</div>
                <div className="text-slate-600">Yes! Validity of the free trial is 15 runs or 7 days, whichever is earlier.</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;